<template>
  <!-- Edit Equipment Dialog -->
  <q-dialog :model-value="modelValue" persistent @update:model-value="$emit('update:modelValue', $event)">
    <q-card style="width: 600px; max-width: 80vw;">
      <q-card-section>
        <div class="text-h6">Edit Equipment</div>
      </q-card-section>

      <q-card-section>
        <q-form @submit.prevent="handleSubmit">
          <q-select
            :model-value="formData.brick_class"
            :options="equipmentBrickClasses"
            label="BRICK Class"
            filled
            required
            emit-value
            map-options
            class="q-mb-md"
            :disable="isTemplatedEquipment({ data: formData })"
            :hint="isTemplatedEquipment({ data: formData }) ? 'Cannot change BRICK class for templated equipment' : ''"
            @update:model-value="$emit('update:brick-class', $event)"
          />
          <q-input
            :model-value="formData.name"
            label="Equipment Name"
            filled
            required
            class="q-mb-md"
            @update:model-value="$emit('update:name', $event)"
          />
          <q-input
            :model-value="formData.description"
            label="Description"
            filled
            type="textarea"
            rows="3"
            class="q-mb-md"
            @update:model-value="$emit('update:description', $event)"
          />

          <!-- Electrical Power Distribution Section -->
          <q-separator class="q-my-md" />
          <div class="text-subtitle2 q-mb-md">Electrical Power Distribution</div>

          <!-- DDC Controller Selection (not for DDC panels themselves) -->
          <q-select
            v-if="formData.brick_class && !formData.brick_class.includes('DDC_Panel')"
            :model-value="formData.controller_id"
            :options="availableDdcPanels.map(p => ({ label: p.name, value: p.id }))"
            label="DDC Controller (Optional)"
            hint="Select the DDC panel that controls this equipment"
            filled
            clearable
            emit-value
            map-options
            class="q-mb-md"
            @update:model-value="$emit('update:controller-id', $event)"
          >
            <template #prepend>
              <q-icon name="memory" color="primary" />
            </template>
            <template #append>
              <q-btn
                v-if="formData.controller_id"
                flat
                round
                dense
                icon="open_in_new"
                size="sm"
                @click.stop="$emit('open-ddc-allocation-dialog')"
              >
                <q-tooltip>Configure in detail</q-tooltip>
              </q-btn>
            </template>
          </q-select>

          <!-- Power Source Selection -->
          <q-select
            :model-value="formData.power_source_id"
            :options="powerSourceOptions"
            label="Power Source (Optional)"
            hint="Select the switchboard that feeds this equipment"
            filled
            clearable
            emit-value
            map-options
            class="q-mb-md"
            @update:model-value="$emit('update:power-source-id', $event)"
          >
            <template #prepend>
              <q-icon name="electrical_services" color="orange" />
            </template>
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                  <q-item-label caption>{{ scope.opt.type }}</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-badge :color="scope.opt.color">
                    {{ scope.opt.badge }}
                  </q-badge>
                </q-item-section>
              </q-item>
            </template>
            <template #append>
              <q-btn
                v-if="formData.power_source_id"
                flat
                round
                dense
                icon="open_in_new"
                size="sm"
                @click.stop="$emit('open-power-connection-dialog')"
              >
                <q-tooltip>Configure in detail</q-tooltip>
              </q-btn>
            </template>
          </q-select>

          <!-- Switchboard Classification (MSSB only) -->
          <q-select
            v-if="formData.brick_class && formData.brick_class.includes('Mechanical_Services_Switchboard')"
            :model-value="formData.power_classification"
            :options="[
              { label: 'Essential', value: 'essential' },
              { label: 'Non-Essential', value: 'non-essential' }
            ]"
            label="Switchboard Classification (Optional)"
            hint="Essential switchboards power critical building systems"
            filled
            clearable
            emit-value
            map-options
            class="q-mb-md"
            @update:model-value="$emit('update:power-classification', $event)"
          >
            <template #prepend>
              <q-icon name="label" color="purple" />
            </template>
          </q-select>

          <!-- DDC Panel Controller Templates (Many-to-Many Management) -->
          <template v-if="formData.brick_class && formData.brick_class.includes('DDC_Panel')">
            <q-separator class="q-my-md" />
            <div class="text-subtitle2 q-mb-md">Controller Templates</div>

            <!-- Load controller templates first -->
            <q-btn
              v-if="!controllerTemplates || controllerTemplates.length === 0"
              outline
              color="primary"
              label="Load Controller Templates"
              icon="refresh"
              class="q-mb-md full-width"
              @click="$emit('load-controller-templates')"
            />

            <!-- List of assigned controllers -->
            <div v-if="formData.ddcControllers && formData.ddcControllers.length > 0" class="q-mb-md">
              <q-list bordered separator>
                <q-item v-for="(ctrl, index) in formData.ddcControllers" :key="index">
                  <q-item-section avatar>
                    <q-icon name="memory" color="primary" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ ctrl.name }}</q-item-label>
                    <q-item-label caption>
                      {{ ctrl.manufacturer }} {{ ctrl.model_number }}
                      <span v-if="ctrl.pivot">
                        - Qty: {{ ctrl.pivot.quantity || 1 }}
                        <span v-if="ctrl.pivot.position_label"> | Position: {{ ctrl.pivot.position_label }}</span>
                      </span>
                    </q-item-label>
                    <q-item-label v-if="ctrl.total_io_points" caption>
                      I/O Points: {{ (ctrl.total_io_points * (ctrl.pivot?.quantity || 1)) }}
                      ({{ ctrl.total_io_points }} × {{ ctrl.pivot?.quantity || 1 }})
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-btn
                      flat
                      round
                      dense
                      icon="delete"
                      color="negative"
                      @click="$emit('remove-controller-from-ddc', index)"
                    >
                      <q-tooltip>Remove controller</q-tooltip>
                    </q-btn>
                  </q-item-section>
                </q-item>
              </q-list>

              <!-- Total I/O Capacity -->
              <q-banner class="bg-positive text-white q-mt-sm rounded-borders">
                <template #avatar>
                  <q-icon name="check_circle" color="white" />
                </template>
                <strong>Total I/O Capacity:</strong> {{ totalIoCapacity }} points
              </q-banner>
            </div>

            <!-- Add Controller Template -->
            <q-card flat bordered class="q-pa-md">
              <div class="text-subtitle2 q-mb-sm">Add Controller Template</div>

              <q-select
                :model-value="ddcControllerToAdd.template_id"
                :options="controllerTemplates.map(t => ({ label: `${t.manufacturer} ${t.model_number} (${t.total_io_points} I/O)`, value: t.id, template: t }))"
                label="Controller Template"
                filled
                emit-value
                map-options
                clearable
                class="q-mb-md"
                @update:model-value="$emit('update:ddc-template-id', $event)"
              >
                <template #prepend>
                  <q-icon name="inventory_2" />
                </template>
              </q-select>

              <div class="row q-col-gutter-sm q-mb-md">
                <div class="col-6">
                  <q-input
                    :model-value="ddcControllerToAdd.quantity"
                    label="Quantity"
                    type="number"
                    min="1"
                    filled
                    @update:model-value="$emit('update:ddc-quantity', $event)"
                  />
                </div>
                <div class="col-6">
                  <q-input
                    :model-value="ddcControllerToAdd.position_label"
                    label="Position (Optional)"
                    filled
                    placeholder="e.g., Slot 1-2"
                    @update:model-value="$emit('update:ddc-position-label', $event)"
                  />
                </div>
              </div>

              <q-btn
                unelevated
                color="primary"
                label="Add Controller"
                icon="add"
                :disable="!ddcControllerToAdd.template_id"
                class="full-width"
                @click="$emit('add-controller-to-ddc')"
              />
            </q-card>
          </template>

          <q-separator class="q-my-md" />

          <!-- Space Assignment for VAVs and Terminal Units -->
          <q-select
            v-if="isVAVOrTerminalUnit(formData)"
            :model-value="formData.serves_space"
            :options="availableSpacesForZone"
            label="Space Served (Optional)"
            hint="Which space this terminal unit feeds"
            filled
            clearable
            emit-value
            map-options
            option-label="label"
            option-value="value"
            @update:model-value="$emit('update:serves-space', $event)"
          />
        </q-form>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="$emit('update:modelValue', false)" />
        <q-btn color="primary" label="Update Equipment" :loading="saving" @click="handleSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { defineProps, defineEmits } from 'vue';

  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
    equipmentBrickClasses: {
      type: Array,
      required: true,
    },
    availableDdcPanels: {
      type: Array,
      default: () => [],
    },
    powerSourceOptions: {
      type: Array,
      default: () => [],
    },
    controllerTemplates: {
      type: Array,
      default: () => [],
    },
    ddcControllerToAdd: {
      type: Object,
      required: true,
    },
    totalIoCapacity: {
      type: Number,
      default: 0,
    },
    availableSpacesForZone: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
    isTemplatedEquipment: {
      type: Function,
      required: true,
    },
    isVAVOrTerminalUnit: {
      type: Function,
      required: true,
    },
  });

  const emit = defineEmits([
    'update:modelValue',
    'update:name',
    'update:brick-class',
    'update:description',
    'update:controller-id',
    'update:power-source-id',
    'update:power-classification',
    'update:serves-space',
    'update:ddc-template-id',
    'update:ddc-quantity',
    'update:ddc-position-label',
    'open-ddc-allocation-dialog',
    'open-power-connection-dialog',
    'load-controller-templates',
    'remove-controller-from-ddc',
    'add-controller-to-ddc',
    'submit',
  ]);

  const handleSubmit = () => {
    emit('submit');
  };
</script>
